import React, { useState } from "react";
import { cn } from "@/lib/utils";

export interface ImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  fill?: boolean;
  sizes?: string;
  quality?: number;
  priority?: boolean;
  loading?: "lazy" | "eager";
  placeholder?: "blur" | "empty";
  blurDataURL?: string;
  className?: string;
  onLoadingComplete?: () => void;
  unoptimized?: boolean;
}

const Image: React.FC<ImageProps> = ({
  src,
  alt,
  width,
  height,
  fill = false,
  sizes,
  quality,
  priority = false,
  loading = "lazy",
  placeholder = "empty",
  blurDataURL,
  className,
  onLoadingComplete,
  unoptimized = false,
  style,
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const handleLoad = () => {
    setIsLoading(false);
    setHasError(false);
    onLoadingComplete?.();
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const loadingAttr = priority ? "eager" : loading;
  const showBlurPlaceholder =
    placeholder === "blur" && blurDataURL && isLoading;

  const imageStyles: React.CSSProperties = {
    ...style,
    width: fill ? "100%" : width,
    height: fill ? "100%" : height,
    objectFit: fill ? "cover" : "contain",
    objectPosition: "center",
  };

  return (
    <div
      className={cn(
        "relative overflow-hidden",
        fill && "absolute inset-0",
        className,
      )}
      style={{
        width: fill ? "100%" : width,
        height: fill ? "100%" : height,
        position: fill ? "absolute" : "relative",
      }}
    >
      {showBlurPlaceholder && (
        <img
          src={blurDataURL}
          alt=""
          className="absolute inset-0 w-full h-full object-cover filter blur-md scale-110"
          aria-hidden="true"
        />
      )}

      {isLoading && !showBlurPlaceholder && (
        <div className="absolute inset-0 bg-muted animate-pulse" />
      )}

      {hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <div className="text-muted-foreground text-sm">
            Failed to load image
          </div>
        </div>
      )}

      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={loadingAttr}
        sizes={sizes}
        className={cn(
          "transition-opacity duration-300",
          isLoading ? "opacity-0" : "opacity-100",
          fill && "absolute inset-0 w-full h-full",
        )}
        style={imageStyles}
        onLoad={handleLoad}
        onError={handleError}
        {...props}
      />
    </div>
  );
};

export { Image };
export default Image;
